import { Injectable } from '@angular/core';
import { rethrowAsSapError, SapError } from 'src/app/lego-utils';
import { isEmpty, uniqBy } from 'lodash-es';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ITablePagination } from 'src/app/models/domain';
import {
  EntryCodeAdapter,
  NotificationAdapter,
  NotificationItemAdapter,
} from 'src/app/models/domain/adapters';
import {
  HeaderSetService,
  ItemsSetService,
  Wrapper1,
  YPMNOTIFICATIONSRVHeader,
  YPMNOTIFICATIONSRVItems,
} from 'src/generated-clients/notification';

interface ICountable extends Wrapper1 {
  __count: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationDataService {
  private readonly equipmentNumberKey: keyof YPMNOTIFICATIONSRVHeader = 'equipmentNumber';
  private readonly entryTypeKey: keyof YPMNOTIFICATIONSRVHeader = 'notificationType';
  private readonly entryCodeKey: keyof YPMNOTIFICATIONSRVItems = 'partOfObject';
  private readonly entryCodeDescriptionKey: keyof YPMNOTIFICATIONSRVItems = 'causeShortText';

  public constructor(
    private readonly notificationService: HeaderSetService,
    private readonly notificationItemsService: ItemsSetService,
    private readonly notificationAdapter: NotificationAdapter,
    private readonly entryCodeAdapter: EntryCodeAdapter,
    private readonly notificationItemAdapter: NotificationItemAdapter
  ) {}

  public getByEquipmentNumberAndNotificationType(
    equipmentNumber: string,
    notificationTypes: string[],
    pagination?: ITablePagination
  ) {
    if (isEmpty(notificationTypes)) {
      return of([]);
    }

    const notificationTypesFilter = `(${notificationTypes
      .map((type) => `${this.entryTypeKey} eq '${type}'`)
      .join(' or ')})`;

    const filter = `${this.equipmentNumberKey} eq '${equipmentNumber}' and ${notificationTypesFilter}`;

    return this.notificationService
      .headerSetGet(
        pagination ? pagination.perPage : undefined,
        undefined,
        filter,
        undefined,
        ['notificationType desc', 'notificationNumber desc'],
        undefined,
        ['toItems']
      )
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d?.results) {
            const errorMsg = `Could not find notifications for equipmentNumber ${equipmentNumber}`;

            throw new Error(errorMsg);
          }

          return response.d.results.map((notification) =>
            this.notificationAdapter.adaptToDomain(notification)
          );
        })
      );
  }

  public loadNotificationPage(equipmentNumber: string, pagination: ITablePagination) {
    const { perPage, page } = pagination;

    const filter = `${this.equipmentNumberKey} eq '${equipmentNumber}'`;

    return this.notificationService
      .headerSetGet(perPage, page > 1 ? (page - 1) * perPage : undefined, filter, 'allpages', [
        'createdDate desc',
      ])
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d?.results) {
            const errorMsg = `Could not find mould logs for equipmentNumber ${equipmentNumber}`;
            throw new Error(errorMsg);
          }

          return {
            data: response.d.results.map((notification) =>
              this.notificationAdapter.adaptToDomain(notification)
            ),
            total: (response.d as ICountable).__count,
          };
        })
      );
  }

  public getConstructionLogPage(
    pagination: ITablePagination,
    equipmentNumber: string,
    entryTypes: string[],
    entryCodes?: string[]
  ) {
    const equipmentNumberFilter = `${this.equipmentNumberKey} eq '${equipmentNumber}'`;

    const entryTypesFilter = `(${entryTypes
      .map((type) => `${this.entryTypeKey} eq '${type}'`)
      .join(' or ')})`;

    const filters = [equipmentNumberFilter, entryTypesFilter];

    if (entryCodes !== undefined && !isEmpty(entryCodes)) {
      const entryCodesFilter = `(${entryCodes
        .map((notificationCode) => `${this.entryCodeKey} eq '${notificationCode}'`)
        .join(' or ')})`;

      filters.push(entryCodesFilter);
    }

    const { page, perPage } = pagination;

    return this.notificationItemsService
      .itemsSetGet(
        perPage,
        page > 1 ? (page - 1) * perPage : undefined,
        filters.join(' and '),
        'allpages',
        ['itemNumber desc'] // sorts by newest log entries first
      )
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d?.results) {
            throw new SapError('Sap Error');
          }

          return {
            data: response.d.results.map((log) => this.notificationItemAdapter.adaptToDomain(log)),
            total: (response.d as ICountable).__count,
          };
        })
      );
  }

  public getEntryCodesByEquipmentNumber(equipmentNumber: string, entryTypes: string[]) {
    if (isEmpty(entryTypes)) {
      return of([]);
    }

    const select = [this.entryCodeKey, this.entryCodeDescriptionKey];

    const entryTypesFilter = `(${entryTypes
      .map((type) => `${this.entryTypeKey} eq '${type}'`)
      .join(' or ')})`;

    const filter = `${this.equipmentNumberKey} eq '${equipmentNumber}' and ${entryTypesFilter}`;

    return this.notificationItemsService
      .itemsSetGet(undefined, undefined, filter, undefined, undefined, select)
      .pipe(
        rethrowAsSapError(),
        map((response) => {
          if (!response.d?.results) {
            throw new Error('SAP Error');
          }

          const codes = response.d.results
            .filter((result) => !!result.partOfObject)
            .map((result) => this.entryCodeAdapter.adaptToDomain(result));

          return uniqBy(codes, (item) => item.code);
        })
      );
  }
}
